import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';

import { Gate } from '../../../global/contexts/auth/auth';
import { api } from '../../../core/network/api';
import { formatDateOnly } from '../../../common/utils/date';
import { animalKeys } from '../../animals/queries/keys';
import { animalPaths } from '../../animals/queries/paths';
import { recipientPaths } from '../../recipients/queries/paths';

import '../../../common/styles/catalog.css';
import '../styles/detail.css';

// Tab Sesiones del detalle de cliente: reúne (por-entidad) las sesiones OPU de sus donadoras
// (opu-breakdown, admin-animals.md · 0109) y las T.E. de sus receptoras (recipient view=full ·
// history). No hay endpoint "sesiones del cliente" → 1 request por donadora + 1 por receptora
// (estas últimas cacheadas por el tab Receptoras). Desglose de estado desde los transfers.

// Estado de preñez → bucket del desglose + etiqueta/punto.
const STATE = {
    pregnant:  { label: 'Preñada',  bucket: 'preg',  dot: 'ses-dot--preg' },
    confirmed: { label: 'Preñada',  bucket: 'preg',  dot: 'ses-dot--preg' },
    born:      { label: 'Parto',    bucket: 'born',  dot: 'ses-dot--born' },
    open:      { label: 'En curso', bucket: 'open',  dot: 'ses-dot--open' },
    empty:     { label: 'Vacía',    bucket: 'empty', dot: 'ses-dot--empty' },
    lost:      { label: 'Perdida',  bucket: 'lost',  dot: 'ses-dot--lost' },
};
const stateInfo = (s) => STATE[s] || { label: s || '—', bucket: 'open', dot: 'ses-dot--open' };
const BUCKETS = [
    ['preg',  'Preñadas', 'ses-dot--preg'],
    ['open',  'En curso', 'ses-dot--open'],
    ['empty', 'Vacías',   'ses-dot--empty'],
    ['lost',  'Perdidas', 'ses-dot--lost'],
    ['born',  'Partos',   'ses-dot--born'],
];

export default function SessionsTab({ donors = [], recipients = [] }) {
    // OPU por donadora (opu-breakdown) — reusa la key del panel de la donadora.
    const donorQ = useQueries({
        queries: donors.map((d) => ({
            queryKey: animalKeys.opuBreakdown(d.id),
            queryFn:  ({ signal }) => api.get(animalPaths.opuBreakdown(d.id), { signal }).then(r => r.data),
            enabled:  !!d.id,
            staleTime: 60_000,
        })),
    });
    // T.E. por receptora (view=full → history) — misma key que useRecipient(id,'full') → caché compartida.
    const recipQ = useQueries({
        queries: recipients.map((r) => ({
            queryKey: ['recipients', 'detail', r.id, 'full'],
            queryFn:  ({ signal }) => api.get(recipientPaths.detail(r.id), { params: { view: 'full' }, signal }).then(res => res.data),
            enabled:  !!r.id,
            staleTime: 60_000,
        })),
    });

    const loading = donorQ.some((q) => q.isLoading) || recipQ.some((q) => q.isLoading);

    // OPU: dedup por opu_session_id; anota qué donadoras del cliente participaron y suma embriones.
    const opuMap = new Map();
    donorQ.forEach((q, i) => {
        const donor = donors[i];
        (q.data?.sessions ?? []).forEach((s) => {
            let e = opuMap.get(s.opu_session_id);
            if (!e) { e = { id: s.opu_session_id, date: s.date, embryos: 0, donors: new Set() }; opuMap.set(s.opu_session_id, e); }
            e.embryos += Number(s.embryos_produced) || 0;
            const label = donor?.name || donor?.number;
            if (label) e.donors.add(label);
        });
    });
    const opuSessions = [...opuMap.values()]
        .map((e) => ({ ...e, donors: [...e.donors] }))
        .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

    // T.E.: un renglón por transfer de las receptoras del cliente.
    const transfers = [];
    recipQ.forEach((q, i) => {
        const rec = recipients[i];
        (q.data?.history ?? []).forEach((h) => {
            transfers.push({
                key:       h.transfer_id,
                code:      h.te_session?.code || (h.te_session?.id ? `TE-${h.te_session.id}` : '—'),
                date:      h.te_date,
                recipient: rec?.number || rec?.name || `#${rec?.id}`,
                state:     h.pregnancy?.state ?? 'open',
            });
        });
    });
    transfers.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

    // Desglose de estado (desde los transfers).
    const counts = { preg: 0, open: 0, empty: 0, lost: 0, born: 0 };
    transfers.forEach((t) => { counts[stateInfo(t.state).bucket]++; });
    const embryosTotal = opuSessions.reduce((a, s) => a + s.embryos, 0);

    if (!donors.length && !recipients.length) {
        return (
            <section className="client-detail__card">
                <p className="client-detail__muted">Este cliente no tiene donadoras ni receptoras, así que no hay sesiones asociadas.</p>
            </section>
        );
    }

    return (
        <div className="ses">
            <section className="client-detail__card">
                <h2 className="client-detail__section-title">Desglose de estado</h2>
                {loading ? (
                    <p className="client-detail__muted">Cargando sesiones…</p>
                ) : (
                    <div className="ses-breakdown">
                        {BUCKETS.map(([k, label, dot]) => (
                            <div key={k} className="ses-stat">
                                <span className="ses-stat__n">{counts[k]}</span>
                                <span className="ses-stat__l"><span className={`ses-dot ${dot}`} aria-hidden="true" />{label}</span>
                            </div>
                        ))}
                        <div className="ses-stat ses-stat--sep"><span className="ses-stat__n">{opuSessions.length}</span><span className="ses-stat__l">Sesiones OPU</span></div>
                        <div className="ses-stat"><span className="ses-stat__n">{embryosTotal}</span><span className="ses-stat__l">Embriones</span></div>
                        <div className="ses-stat"><span className="ses-stat__n">{transfers.length}</span><span className="ses-stat__l">T.E.</span></div>
                    </div>
                )}
            </section>

            <section className="client-detail__card">
                <div className="client-detail__section-head">
                    <h2 className="client-detail__section-title">Sesiones OPU {!loading && <span className="client-detail__count">{opuSessions.length}</span>}</h2>
                    <span className="client-detail__muted">de sus donadoras</span>
                </div>
                {loading ? (
                    <p className="client-detail__muted">Cargando…</p>
                ) : !opuSessions.length ? (
                    <p className="client-detail__muted">Sin sesiones OPU de sus donadoras.</p>
                ) : (
                    <table className="cat-table cat-table--compact">
                        <thead><tr><th>Sesión</th><th>Fecha</th><th>Donadoras</th><th className="cat-table__num">Embriones</th></tr></thead>
                        <tbody>
                            {opuSessions.map((s) => (
                                <tr key={s.id} className="cat-table__row">
                                    <td>
                                        <Gate domain="aspirations" action="read" fallback={<span className="cat-table__name">OPU #{s.id}</span>}>
                                            <Link to={`/admin/opu/${s.id}`} className="cat-table__name">OPU #{s.id}</Link>
                                        </Gate>
                                    </td>
                                    <td>{formatDateOnly(s.date) || '—'}</td>
                                    <td>{s.donors.join(', ') || <span className="cat-table__empty">—</span>}</td>
                                    <td className="cat-table__num">{s.embryos}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <section className="client-detail__card">
                <div className="client-detail__section-head">
                    <h2 className="client-detail__section-title">Transferencias (T.E.) {!loading && <span className="client-detail__count">{transfers.length}</span>}</h2>
                    <span className="client-detail__muted">a sus receptoras</span>
                </div>
                {loading ? (
                    <p className="client-detail__muted">Cargando…</p>
                ) : !transfers.length ? (
                    <p className="client-detail__muted">Sin transferencias a sus receptoras.</p>
                ) : (
                    <table className="cat-table cat-table--compact">
                        <thead><tr><th>Sesión</th><th>Fecha</th><th>Receptora</th><th>Estado</th></tr></thead>
                        <tbody>
                            {transfers.map((t) => {
                                const st = stateInfo(t.state);
                                return (
                                    <tr key={t.key} className="cat-table__row">
                                        <td className="cat-table__name">{t.code}</td>
                                        <td>{formatDateOnly(t.date) || '—'}</td>
                                        <td>{t.recipient}</td>
                                        <td><span className="ses-badge"><span className={`ses-dot ${st.dot}`} aria-hidden="true" />{st.label}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
